import type { ReactNode } from 'react';

export type ChipTone = 'success' | 'warning' | 'danger' | 'in-progress' | 'neutral';

type Props = {
  tone?: ChipTone;
  icon?: ReactNode;
  pulse?: boolean;
  children: ReactNode;
  className?: string;
};

const toneClass: Record<ChipTone, string> = {
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-soft text-danger',
  'in-progress': 'bg-neutral-100 text-neutral-700',
  neutral: 'bg-neutral-100 text-neutral-700',
};

/**
 * Status chip / badge. Pill-shaped, icon + label (never color-only).
 * In-progress chips carry motion, not a third semantic color.
 */
export function Chip({ tone = 'neutral', icon, pulse = false, children, className = '' }: Props) {
  return (
    <span
      className={
        'rounded-pill inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium ' +
        toneClass[tone] +
        (className ? ` ${className}` : '')
      }
    >
      {icon && (
        <span
          aria-hidden="true"
          className={
            'inline-flex shrink-0 [&_svg]:h-3.5 [&_svg]:w-3.5 ' + (pulse ? 'animate-pulse' : '')
          }
        >
          {icon}
        </span>
      )}
      <span>{children}</span>
    </span>
  );
}

/**
 * Outline chip — used for counter chips (e.g. "Hoy: N ventas") that shouldn't
 * carry semantic color. Border + neutral surface.
 */
export function ChipOutline({
  icon,
  children,
  className = '',
}: {
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={
        'rounded-pill inline-flex items-center gap-1.5 border border-neutral-200 bg-neutral-100 px-2.5 py-0.5 text-xs font-medium text-neutral-700 ' +
        className
      }
    >
      {icon && (
        <span aria-hidden="true" className="inline-flex shrink-0 [&_svg]:h-3 [&_svg]:w-3">
          {icon}
        </span>
      )}
      <span>{children}</span>
    </span>
  );
}
